/**
 * API Provider Registry
 * Manages registration and lifecycle of API providers
 */

import { ApiProvider, AssetType } from '@/types/api';
import { CryptowatchRProvider } from './providers/cryptowatchrProvider';
import { TwelvedataProvider } from './providers/twelvedataProvider';
import { CoinGeckoProvider } from './providers/coingeckoProvider';
import { CoinMarketCapProvider } from './providers/coinmarketcapProvider';
import { AlphaVantageProvider } from './providers/alphavantageProvider';
import { FinnhubProvider } from './providers/finnhubProvider';
import { MarketstackProvider } from './providers/marketstackProvider';
import { FinancialModelingPrepProvider } from './providers/financialModelingPrepProvider';
import { AktoolsProvider } from './providers/aktoolsProvider';

/**
 * Provider Registry Class
 */
export class ProviderRegistry {
  private providers = new Map<string, ApiProvider>();
  private healthCheckInterval: NodeJS.Timeout | null = null;
  private readonly healthCheckIntervalMs = 5 * 60 * 1000; // 5 minutes
  
  constructor() {
    this.initializeProviders();
    // Only start periodic health checks in browser/Tauri runtime.
    // In Node scripts/tests this would keep the process alive and can cause noisy network calls.
    if (typeof window !== 'undefined') {
      this.startHealthChecks();
    }
  }
  
  /**
   * Get provider by name
   */
  getProvider(name: string): ApiProvider | undefined {
    return this.providers.get(name);
  }
  
  /**
   * Get all providers
   */
  getAllProviders(): ApiProvider[] {
    return Array.from(this.providers.values());
  }
  
  /**
   * Get healthy providers for asset type
   */
  getHealthyProviders(assetType?: AssetType): ApiProvider[] {
    return Array.from(this.providers.values())
      .filter(provider => {
        const isHealthy = provider.isHealthy;
        const supportsType = !assetType || provider.supportedAssetTypes.includes(assetType);
        return isHealthy && supportsType;
      })
      .sort((a, b) => a.priority - b.priority);
  }
  
  /**
   * Register a new provider
   */
  registerProvider(provider: ApiProvider): void {
    this.providers.set(provider.name, provider);
    console.log(`Registered API provider: ${provider.name}`);
  }
  
  /**
   * Unregister a provider
   */
  unregisterProvider(name: string): void {
    if (this.providers.delete(name)) {
      console.log(`Unregistered API provider: ${name}`);
    }
  }
  
  /**
   * Reset provider health status
   */
  resetProviderHealth(providerName?: string): void {
    if (providerName) {
      const provider = this.providers.get(providerName);
      if (provider) {
        provider.isHealthy = true;
        delete provider.lastError;
        console.log(`Reset health status for provider: ${providerName}`);
      }
    } else {
      for (const provider of this.providers.values()) {
        provider.isHealthy = true;
        delete provider.lastError;
      }
      console.log('Reset health status for all providers');
    }
  }
  
  /**
   * Mark provider as unhealthy
   */
  markProviderUnhealthy(providerName: string, error?: Error): void {
    const provider = this.providers.get(providerName);
    if (provider) {
      provider.isHealthy = false;
      provider.lastError = new Date();
      console.warn(`Marked provider as unhealthy: ${providerName}`, error?.message);
    }
  }
  
  /**
   * Get provider statistics
   */
  getProviderStats(): Record<string, {
    name: string;
    isHealthy: boolean;
    lastError?: Date;
    supportedAssetTypes: AssetType[];
    priority: number;
  }> {
    const stats: Record<string, any> = {};
    
    for (const [name, provider] of this.providers.entries()) {
      stats[name] = {
        name: provider.name,
        isHealthy: provider.isHealthy,
        lastError: provider.lastError,
        supportedAssetTypes: provider.supportedAssetTypes,
        priority: provider.priority
      };
    }
    
    return stats;
  }
  
  /**
   * Start periodic health checks (e.g. when app becomes visible again).
   * Stops when app is closed/hidden; call startHealthChecks() when visible again per remember.md.
   */
  startHealthChecks(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
    }
    
    this.healthCheckInterval = setInterval(() => {
      this.performHealthChecks();
    }, this.healthCheckIntervalMs);
    
    console.log('Started provider health checks');
  }
  
  /**
   * Stop health checks
   */
  stopHealthChecks(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
      console.log('Stopped provider health checks');
    }
  }
  
  /**
   * Perform health checks on all providers
   */
  private async performHealthChecks(): Promise<void> {
    console.log('Performing provider health checks...');
    
    const healthCheckPromises = Array.from(this.providers.values()).map(async (provider) => {
      try {
        const isHealthy = await provider.testConnection({ timeout: 5000 });
        
        if (isHealthy && !provider.isHealthy) {
          // Provider recovered
          provider.isHealthy = true;
          delete provider.lastError;
          console.log(`Provider recovered: ${provider.name}`);
        } else if (!isHealthy && provider.isHealthy) {
          // Provider became unhealthy
          this.markProviderUnhealthy(provider.name, new Error('Health check failed'));
        }
      } catch (error) {
        if (provider.isHealthy) {
          this.markProviderUnhealthy(
            provider.name,
            error instanceof Error ? error : new Error('Health check failed')
          );
        }
      }
    });
    
    await Promise.allSettled(healthCheckPromises);
  }
  
  /**
   * Initialize default providers
   * Priority order: Lower number = higher priority
   */
  private initializeProviders(): void {
    // Register AKTools provider (priority 0 - self-hosted, supports multiple asset types)
    this.registerProvider(new AktoolsProvider());

    // Register Financial Modeling Prep provider (priority 0.5, index only, specialized for indices, higher priority than TwelveData for index assets)
    this.registerProvider(new FinancialModelingPrepProvider());
    
    // Register Twelvedata provider (priority 1 - highest, supports all asset types)
    this.registerProvider(new TwelvedataProvider());
    
    // Register CoinGecko provider (priority 2 for crypto, free, no key required)
    this.registerProvider(new CoinGeckoProvider());
    
    // Register Alpha Vantage provider (priority 3, supports stock/index/forex/crypto)
    this.registerProvider(new AlphaVantageProvider());
    
    // Register Finnhub provider (priority 4, supports stock/forex/crypto)
    this.registerProvider(new FinnhubProvider());
    
    // Register Marketstack provider (priority 5, supports stock/index)
    this.registerProvider(new MarketstackProvider());
    
    // Register CoinMarketCap provider (priority 6, crypto only, requires key)
    this.registerProvider(new CoinMarketCapProvider());
    
    // Register CryptowatchR provider (priority 7, crypto only, fallback)
    this.registerProvider(new CryptowatchRProvider());
  }
  
  /**
   * Cleanup resources
   */
  destroy(): void {
    this.stopHealthChecks();
    this.providers.clear();
  }
}

// Export singleton instance
export const providerRegistry = new ProviderRegistry();